import { cn, UzButton, UzPopover } from '@utazzo/ui';
import { Check, Coins } from 'lucide-react';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  describeCosts,
  parseCostsParam,
  resolveSelectedCodes,
  serialiseCosts,
} from '../cost-selection';
import { useMarketingCostHeads } from '../use-marketing-reports';

import type { MarketingCostHead } from '@utazzo/shared';

import { useUrlParamWriter } from '@/hooks/use-url-param-writer';

/**
 * "Include costs" picker for the Marketing report, URL-driven via `?costs=`.
 *
 * Which costs go into a month's Total Spend is the operator's decision, made
 * here, and never assumed: an unticked head is out of every cost-derived figure
 * and out of the completeness gate. A fresh visit ticks every active head.
 *
 * Picks are STAGED and committed on Apply, like the month picker - each change
 * refetches the two cost-bearing aggregates, so committing per click would
 * spend requests on a selection still being assembled.
 */

const modeLabel = (head: MarketingCostHead): string => {
  if (head.mode === 'CARRY_FORWARD') return 'Carried forward';
  if (head.mode === 'PER_MONTH') return 'Per month';
  return `Calculated${head.rate !== null ? ` · ₹${head.rate}/unit` : ''}`;
};

export const CostFilter = (): React.JSX.Element => {
  const [params] = useSearchParams();
  const writeParams = useUrlParamWriter();
  const heads = useMarketingCostHeads();
  const active = (heads.data?.data ?? []).filter((head) => head.isActive);
  const activeCodes = active.map((head) => head.code);
  const selected = resolveSelectedCodes(parseCostsParam(params.get('costs')), activeCodes);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(selected);

  const onOpenChange = (next: boolean): void => {
    // Re-seeded from the URL each time, so an abandoned draft is discarded.
    if (next) setDraft(resolveSelectedCodes(parseCostsParam(params.get('costs')), activeCodes));
    setOpen(next);
  };

  const toggle = (code: string): void => {
    setDraft((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const apply = (): void => {
    writeParams((p) => {
      // Every head ticked = the default, so the URL stays clean.
      if (draft.length === activeCodes.length) p.delete('costs');
      else p.set('costs', serialiseCosts(draft));
    });
    setOpen(false);
  };

  return (
    <UzPopover
      open={open}
      onOpenChange={onOpenChange}
      align="end"
      className="w-80 p-0"
      trigger={
        <UzButton variant="outline" size="sm" className="h-9" disabled={heads.isLoading}>
          <Coins className="mr-2 h-4 w-4" />
          {describeCosts(selected, activeCodes.length)}
        </UzButton>
      }
    >
      <div className="flex flex-col">
        <p className="text-muted-foreground border-b border-(--border) px-3 py-2 text-xs">
          Ticked costs are added to Total Spend and every figure built on it. Ad spend from the
          Campaigns tab is always included.
        </p>

        {active.length === 0 ? (
          <p className="text-muted-foreground px-3 py-4 text-sm">
            No cost heads yet. Add them under Marketing Cost Heads.
          </p>
        ) : (
          <ul className="max-h-72 overflow-y-auto py-1">
            {active.map((head) => {
              const picked = draft.includes(head.code);
              return (
                <li key={head.code}>
                  <button
                    type="button"
                    aria-pressed={picked}
                    onClick={() => toggle(head.code)}
                    className="hover:bg-muted flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors"
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        picked
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-(--border)',
                      )}
                    >
                      {picked && <Check className="h-3 w-3" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="text-foreground block truncate text-sm">
                        {head.name}
                        {head.isRequired && (
                          <span className="text-muted-foreground ml-1 text-[11px]">· required</span>
                        )}
                      </span>
                      <span className="text-muted-foreground block truncate text-[11px]">
                        {modeLabel(head)}
                        {head.querySourceName ? ` · ${head.querySourceName}` : ' · all channels'}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-(--border) px-3 py-2">
          <span className="text-muted-foreground text-xs">
            {describeCosts(draft, activeCodes.length)}
          </span>
          <div className="flex items-center gap-1">
            <UzButton
              variant="ghost"
              size="sm"
              onClick={() => setDraft(draft.length === activeCodes.length ? [] : [...activeCodes])}
              disabled={active.length === 0}
            >
              {draft.length === activeCodes.length ? 'None' : 'All'}
            </UzButton>
            <UzButton size="sm" onClick={apply} disabled={active.length === 0}>
              Apply
            </UzButton>
          </div>
        </div>
      </div>
    </UzPopover>
  );
};
